import { useLocation, useParams } from "react-router";
import { ErrValidationFailedContextProvider } from "@/shared/lib/errors/err-validation-failed-context";
import { useFeatureAuth } from "@/services/backend/auth/hooks/use-feature-auth";
import { WorkOrderStateDropdown } from "@/services/backend/relationships/components/work-order-state-dropdown";
import {
  useRltWorkOrderShowQuery,
  useRltWorkOrderUpdateWorkOrderStateMutation,
} from "@/services/backend/rlt/work-orders/api/work-order-api";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import t from "@/lang/lang";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  DocumentPageNavigation,
  DocumentPageNavigationLink,
  DocumentPageNavigationOutlet,
  DocumentPageSkeleton,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { WorkOrderPdf } from "@/services/backend/rlt/work-orders/components/work-order-pdf";
import { WorkOrder } from "@/services/backend/rlt/work-orders/types/work-order";
import { Separator } from "@/shared/components/ui/separator";
import { useWorkOrderStateGuard } from "@/routes/processes/[processId]/rlt/work-orders/_components/use-work-order-state-guard";
import { FulfillmentState } from "@/services/backend/relationships/types/fulfillment";
import { SignatureButton } from "@/services/backend/rlt/work-orders/components/signature-button";
import { AddExtraWorkDuringExecutionDropDown } from "@/routes/processes/[processId]/rlt/work-orders/execution/[workOrderId]/treatment/_components/add-extra-work-during-execution-drop-down";
import { ProcessPageMainTemplate } from "@/routes/processes/[processId]/_components/process-page-main-template";
import { WorkOrderBreadcrumbs } from "@/routes/processes/[processId]/rlt/work-orders/_components/work-order-breadcrumbs";

export function WorkOrderExecutionRoute() {
  const { workOrderId } = useParams();
  const {
    data: workOrder,
    isLoading,
    error,
  } = useRltWorkOrderShowQuery({
    id: workOrderId!,
  });

  useWorkOrderStateGuard(workOrder, FulfillmentState.Execution);

  const breadcrumbs = workOrder && (
    <WorkOrderBreadcrumbs workOrder={workOrder} segment="execution">
      {t("Ausführung")}
    </WorkOrderBreadcrumbs>
  );

  return (
    <ProcessPageMainTemplate breadcrumbs={breadcrumbs}>
      {error && <RTKQueryErrorAlert error={error} />}
      {isLoading && <DocumentPageSkeleton />}
      {workOrder && (
        <PersistenceContextProvider>
          <ErrValidationFailedContextProvider>
            <Page workOrder={workOrder} />
          </ErrValidationFailedContextProvider>
        </PersistenceContextProvider>
      )}
    </ProcessPageMainTemplate>
  );
}

function Page({ workOrder }: { workOrder: WorkOrder }) {
  // The gate of the deleted BackToPlanningDialog: only some roles
  // may hand a work order back to the planning.
  const canBackToPlanning = useFeatureAuth(
    "gsm.rlt.workorder.back-to-planning",
  );

  // The field names of each tab: the keys of workorder.ReadyForCustomer and
  // of the validation errors of the fields shown on the tab.
  const links = [
    {
      to: "base-data",
      fieldName: [
        "referenceNumber",
        "executingBranchId",
        "beginDate",
        "endDate",
        "teamLeaderId",
        "customerReference",
        "internalComment",
        "serviceAddress",
        "serviceContact",
        "representative",
      ],
      label: t("Daten"),
    },
    {
      to: `last-minute-risk-analysis/${workOrder.lastMinuteRiskAnalysisId}`,
      fieldName: ["lastMinuteRiskAnalysis"],
      label: t("LMRA"),
    },
    {
      to: "treatment",
      label: t("Behandlung"),
    },
    {
      to: `documentation/${workOrder.documentationId}`,
      fieldName: ["documentation"],
      label: t("Dokumentation"),
    },
    {
      to: "files",
      label: t("Dateien"),
    },
  ];

  const location = useLocation();

  return (
    <DocumentPage>
      <DocumentPageHeader>
        <DocumentPageNavigation>
          {links.map((link) => (
            <DocumentPageNavigationLink
              key={link.to}
              to={link.to}
              fieldName={link.fieldName}
            >
              {link.label}
            </DocumentPageNavigationLink>
          ))}
        </DocumentPageNavigation>
        <Separator orientation="vertical" className="max-h-8" />
        <DocumentPageActions>
          <PersistenceStateUsingContext className="mr-2" />
          {location.pathname.endsWith("treatment") && (
            <>
              <Separator orientation="vertical" className="min-h-8" />
              <AddExtraWorkDuringExecutionDropDown workOrder={workOrder} />
            </>
          )}
          <Separator orientation="vertical" className="min-h-8" />
          <SignatureButton workOrderID={workOrder.id} />
          <WorkOrderPdf workOrder={workOrder} />
          <Separator orientation="vertical" className="min-h-8" />
          <WorkOrderStateDropdown
            workOrder={workOrder}
            mutation={useRltWorkOrderUpdateWorkOrderStateMutation}
            transitions={[
              ...(canBackToPlanning ? [FulfillmentState.Planning] : []),
              FulfillmentState.Executed,
              FulfillmentState.PartiallyExecuted,
            ]}
          />
        </DocumentPageActions>
      </DocumentPageHeader>
      <DocumentPageContent>
        <DocumentPageNavigationOutlet />
      </DocumentPageContent>
    </DocumentPage>
  );
}
